import React from 'react';
import Section from './Section';
import AddRisk from '../../Forms/AddRisk';
import { Body, bodySmall } from '../../../../Constants/Fonts';
import { LIKELIHOOD_LABELS, IMPACT_LABELS, titleCase } from './constants';

export default function AssessmentSection({
    riskId, detail, role, notify, submitStage, canSubmit, stageBtnText, busy,
    categories, businessUnits, projects, onCreated,
}) {
    // No risk yet (Create Risk opened the workflow): the same form, empty, and
    // saving it creates the risk.
    if (riskId == null) {
        return (
            <Section title="Risk Assessment">
                <AddRisk
                    inline
                    categories={categories}
                    businessUnits={businessUnits}
                    projects={projects}
                    role={role}
                    submitText={stageBtnText}
                    submitLoadingText="Creating…"
                    onSaved={onCreated}
                    onError={(msg) => notify?.(msg || 'Failed to create risk', 'error')}
                />
            </Section>
        );
    }

    const a = detail.assessment || {};

    // Read-only view (when not the acting owner) — key : value list.
    const scaleVal = (n, labels) => (n >= 1 && n <= labels.length ? `${n} — ${labels[n - 1]}` : (n != null ? String(n) : undefined));
    const roItems = [
        ['Inherent Likelihood', scaleVal(a.inherent_likelihood ?? detail.likelihood, LIKELIHOOD_LABELS)],
        ['Inherent Impact', scaleVal(a.inherent_impact ?? detail.impact, IMPACT_LABELS)],
        ['Residual Likelihood', scaleVal(a.residual_likelihood, LIKELIHOOD_LABELS)],
        ['Residual Impact', scaleVal(a.residual_impact, IMPACT_LABELS)],
        ['Control Effectiveness', a.control_effectiveness != null ? `${a.control_effectiveness}%` : undefined],
        ['Risk Velocity', a.risk_velocity ? titleCase(a.risk_velocity) : undefined],
    ].filter(([, v]) => v != null && v !== '');

    return (
        <Section title="Risk Assessment">
            {canSubmit ? (
                <AddRisk
                    inline
                    editingRisk={{ ...detail, id: detail.id ?? riskId }}
                    categories={categories}
                    businessUnits={businessUnits}
                    projects={projects}
                    role={role}
                    submitText={stageBtnText}
                    submitLoadingText="Saving…"
                    onSaved={() => submitStage(null)}
                    onError={(msg) => notify?.(msg || 'Failed to save risk', 'error')}
                />
            ) : roItems.length === 0 && !a.notes ? (
                <p style={bodySmall({ color: 'var(--color-text-muted)', margin: 0 })}>No assessment data yet.</p>
            ) : (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
                        {roItems.map(([label, value]) => (
                            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: '11px 14px', background: 'var(--color-surface-muted)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)' }}>
                                <Body as="span" variant={2} color="var(--color-text-muted)">{label}</Body>
                                <Body as="span" color="var(--color-text-primary)" weight="semibold" style={{ textAlign: 'right' }}>{value}</Body>
                            </div>
                        ))}
                    </div>
                    {a.notes && (
                        <div style={{ marginTop: 10, padding: '11px 14px', background: 'var(--color-surface-muted)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)' }}>
                            <Body as="div" variant={2} color="var(--color-text-muted)" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Notes</Body>
                            <Body as="div" color="var(--color-text-primary)">{a.notes}</Body>
                        </div>
                    )}
                </>
            )}
        </Section>
    );
}
